import React from 'react'
import Notes from 'components/Notes'
import { Header, Button } from 'components/patterns'
import strings from 'utils/strings'
import { setComponentMeta, setSelectedId } from 'actions/navigator'
import { FEATURE_NOTES, FEATURE_TYPE, FUNCTION_TYPE } from 'utils/constants'
import { EditorInnerStyledContainer } from 'components/feature/FeatureEditor/FeatureEditor.style'
import { useSelector } from 'hooks/redux'
import { getIsSimulating } from 'selectors/navigator'
import { getFeaturesRouteMatch, getIsHistoryRoute } from 'selectors/router'
import { path } from 'ramda'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { StyledNoteBubble } from 'components/NoteCounter/NoteCounter.style'
import { useLocalStorage } from 'hooks'
import { HeaderStyled } from '../editors/Editors.style'

const AllowedTypes = [FEATURE_TYPE, FUNCTION_TYPE]

export default function FeatureNotes({
  selectedId = null,
  fromType,
  description = null,
}) {
  const dispatch = useDispatch()
  // get current feature id
  const match = useSelector(getFeaturesRouteMatch)
  const currentFeatureId = path(['params', 'featureId'], match)
  const isFeature = selectedId === currentFeatureId
  const [{ [selectedId]: draftNote }, setDraftNote] = useLocalStorage(
    `${currentFeatureId}/notes`,
    { [selectedId]: '' } // default value
  )
  // get read only status
  const isHistoryRoute = useSelector(getIsHistoryRoute)
  const isSimulating = useSelector(getIsSimulating)
  const notesAllowed =
    AllowedTypes.includes(fromType) && !isHistoryRoute && !isSimulating
  const readOnly = !notesAllowed
  
  const readOnlyWarning = (() => {
    if (isSimulating)
      return <>Notes cannot be created in Simulator Mode</>
    
    if(isHistoryRoute)
      return <>Notes cannot be created when viewing a Feature&apos;s Version History.</>

    return <>Notes cannot be created for{' '}
      {description ? `"${description}"` : `"${strings.feature}"`}
    </>
    
  })()

  const backToFeature = () => {
    dispatch(setSelectedId(selectedId))
  }
  const handleSaveDraftNote = (_, note) => {
    setDraftNote(() => ({ [selectedId]: note }))
  }

  return (
    <div>
      <TopNav>
        <StyledViewNotesButton link size="small" onClick={backToFeature}>
          &raquo; Back to {description || strings.feature}
        </StyledViewNotesButton>
      </TopNav>
      <EditorInnerStyledContainer style={{ marginLeft: 0 }}>
        <HeaderStyled>
          <Header type="h3">
            <StyledNoteBubble /> {strings.notes}
          </Header>
        </HeaderStyled>
        {isFeature && (
          <Notes
            readOnly={readOnly}
            showReadOnlyWarning={!notesAllowed}
            readOnlyWarning={readOnlyWarning}
            id={currentFeatureId}
            defaultNote={draftNote}
            onChange={handleSaveDraftNote}
          />
        )}
        {!isFeature && (
          <Notes
            readOnly={readOnly}
            showReadOnlyWarning={!notesAllowed}
            readOnlyWarning={readOnlyWarning}
            subType={fromType}
            subDescription={description}
            subUri={selectedId}
            id={currentFeatureId}
            defaultNote={draftNote}
            onChange={handleSaveDraftNote}
          />
        )}
      </EditorInnerStyledContainer>
    </div>
  )
}

FeatureNotes.propTypes = {
  selectedId: PropTypes.string,
  fromType: PropTypes.string,
  description: PropTypes.string,
}

// flex container with child aligned to the right
const TopNav = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 5px;
`

export function ViewNotesButton({ fromType = FEATURE_TYPE, id, description }) {
  const dispatch = useDispatch()
  const viewNotes = () => {
    dispatch(setSelectedId(FEATURE_NOTES))
    dispatch(setComponentMeta(FEATURE_NOTES, {
        selectedId: id,
        type: FEATURE_NOTES,
        fromType,
        description,
      })
    )
  }
  return (
    <TopNav>
      <StyledViewNotesButton link size="small" onClick={viewNotes}>
        &laquo; {strings.view_notes}
      </StyledViewNotesButton>
    </TopNav>
  )
}

ViewNotesButton.propTypes = {
  fromType: PropTypes.string,
  id: PropTypes.string,
  description: PropTypes.string,
}

const StyledViewNotesButton = styled(Button)`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50%;
`
